import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import {
  Gauge, Package, Truck, Leaf, BellRinging, Buildings, SignOut, ScanSmiley, MapTrifold,
} from "@phosphor-icons/react";

const NAV = {
  superadmin: [
    { to: "/tenants", label: "Tenants", icon: Buildings },
    { to: "/carbon", label: "Platform ESG", icon: Leaf },
  ],
  tenant_admin: [
    { to: "/control-tower", label: "Control Tower", icon: Gauge },
    { to: "/shipments", label: "Shipments", icon: Package },
    { to: "/fleet", label: "Fleet & Drivers", icon: Truck },
    { to: "/carbon", label: "ESG & Carbon", icon: Leaf },
    { to: "/alerts", label: "Alerts", icon: BellRinging },
  ],
};

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[user.role] || [];
  useDocumentTitle(title);

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white fixed h-screen">
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <MapTrifold weight="fill" size={20} />
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none tracking-tight">CemTrack</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Control Tower</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <it.icon size={18} weight="duotone" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className="px-3 py-2">
            <div className="text-sm font-semibold text-slate-900 truncate">{user.name}</div>
            <div className="text-xs text-slate-500 truncate">{user.tenant?.name || (user.role === "superadmin" ? "Platform" : "")}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <SignOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ml-64">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 md:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
        <main className="p-5 md:p-8">{children}</main>
      </div>
    </div>
  );
}
